/**
 * `<MapMembers/>` — B6.4, the Tasks-team members surface.
 *
 * Issue #762 (Phase 2, Settings). Replaces the Phase-0 `MapMembersPage` stub.
 * Same membership model as scheduling teams, but the framing is "who's on the
 * map" (§B6.4): each row is name · role · "on N tasks" (a neutral coverage
 * count). Faithful to `B6-4-members.svg`: a header with the team name + a
 * "+ invite" action, then one card per member — the lead row tinted, an
 * avatar initials circle, an indigo "lead" badge for the lead and a
 * plain "member" label otherwise, and the right-aligned "on N tasks".
 *
 * Brand firewall (§A9, banlist): the gallery's own guard is rendered verbatim
 * — "'on N tasks' counts assignments — it is not a ranking or a score." The
 * count is unordered (member rows keep roster order), never a per-person
 * performance metric.
 *
 * Member management seam (Issue #954): the "+ invite" seam is now wired to
 * reuse `routes/team/InviteMemberDialog` directly.
 *
 * DELIBERATE CROSS-IMPORT: `InviteMemberDialog` lives under `routes/team/` (a
 * scheduling-side directory), but the web `taskmap ⊥ scheduler` boundary is a
 * CONVENTION only — there is no machine-enforced linter rule on the frontend
 * side (the linter enforcement is backend-only). The invite dialog itself is
 * app-agnostic: it calls `POST /api/teams/{id}/invites`, an endpoint that
 * carries no scheduling concept, and uses only generic UI primitives. The
 * founder explicitly chose "reuse the existing invite flow" (Issue #954) over
 * a purpose-built taskmap dialog. This import is therefore intentional and
 * accepted; if the dialog later migrates to a `shared/` module, update only
 * this import path.
 *
 * Isolation: only the generic roster hook + taskmap-local primitives + the
 * shared (app-agnostic) InviteMemberDialog.
 *
 * Editorial revamp (#1043, Wave 4, B6.4): theme-aware via `useMapTheme()`. The
 * `task_editorial_ui` flag stays OFF for every team (default LEGACY) until the
 * whole revamp lands, so:
 *   - LEGACY (flag off): byte-identical to the shipped soft-mint surface — the
 *     "Members · <team>" heading, the teal "+ invite", the mint member cards.
 *   - EDITORIAL (flag on): the wireframe `V_B64` — a mono `N ON THIS MAP` kicker
 *     over a serif-italic "Members" heading + an ink "+ invite"; a `.t-card`
 *     member list (a theme-aware avatar disc · name · role · a mono `ON N TASKS`
 *     coverage tag), hairline-divided; the italic "neutral count, not ordered or
 *     scored" footer.
 * Brand firewall holds in BOTH modes: "on N tasks" is a neutral COUNT, never a
 * ranking or score — the §A9 guard line is rendered verbatim in both.
 * RE-SKIN, not rebuild: every `data-testid`, copy, and behaviour is preserved.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useTeam } from "../../api/useTeam";
import { useBreadcrumbs } from "../../components/Breadcrumbs";
import { ErrorState, Skeleton, Tooltip } from "../../components/ui";
import { useAuth } from "../../lib/AuthContext";
// DELIBERATE CROSS-IMPORT — see module docblock above.
import { InviteMemberDialog } from "../../routes/team/InviteMemberDialog";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { PALETTE } from "../tree/palette";
import { EventJoinLinkCard } from "./EventJoinLinkCard";
import { useCurrentMember } from "./useCurrentMember";
import { type RosterMember, useTaskTeamRoster } from "./useTaskTeamRoster";

const INK = "#0C2A2A";
const MUTED = "#9CA3AF";
const INDIGO = "#4F46E5";

/**
 * The editorial avatar tones (`tarii.css` `--av-*` / `Avatar.tsx`): cobalt ·
 * sage · amber · purple · terracotta. Picked deterministically from a member's
 * initials so the same person is the same colour across every Map surface (the
 * "one node reads identically everywhere" integration invariant). Mirrors
 * `components/Avatar.tsx` so the Members list discs match the canvas avatars.
 */
const EDITORIAL_AVATAR_TONES = ["#4566A0", "#5E8E5C", "#C9852D", "#8C5BA1", "#B0593D"] as const;

function editorialTone(initials: string): string {
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = (hash * 31 + initials.charCodeAt(i)) | 0;
  }
  return EDITORIAL_AVATAR_TONES[Math.abs(hash) % EDITORIAL_AVATAR_TONES.length] as string;
}

export function MapMembers() {
  const { teamId } = useParams<{ teamId: string }>();
  const teamQuery = useTeam(teamId);
  const { roster, isLoading, isError } = useTaskTeamRoster(teamId);
  const { isLead } = useCurrentMember(teamId);
  const { accountId } = useAuth();
  const queryClient = useQueryClient();
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // Controlled open/close state for the invite dialog (lead-only).
  const [inviteOpen, setInviteOpen] = useState(false);

  const teamName = teamQuery.data?.name ?? "This team";

  useBreadcrumbs(
    teamId
      ? [{ label: teamName, to: `/teams/${teamId}/map` }, { label: "Members" }]
      : [{ label: "Members" }],
  );

  // When the dialog closes after a successful invite, also invalidate the
  // task-team roster query so the new pending/member row appears immediately.
  // (InviteMemberDialog already invalidates TEAM_INVITES_KEY internally; this
  // ensures the Map's own "task-team-members" query also refetches.)
  const handleInviteOpenChange = (open: boolean) => {
    setInviteOpen(open);
    if (!open && teamId) {
      void queryClient.invalidateQueries({ queryKey: ["task-team-members", teamId] });
    }
  };

  // The editorial invite button is an ink pill; legacy is the teal pill. Both
  // keep the same testid + behaviour (open the dialog / disabled seam).
  const inviteEditorialStyle = { backgroundColor: c.ink, color: c.canvas };
  const inviteLegacyStyle = { backgroundColor: PALETTE.teal };

  return (
    <div
      data-testid="map-members-page"
      className="h-full overflow-auto px-6 py-5"
      style={{
        backgroundColor: editorial ? c.canvas : "#EEF6F2",
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      <div className="mx-auto max-w-3xl">
        {/* Header: title + invite button (lead) or disabled seam (member). */}
        <div className={`flex items-center justify-between ${editorial ? "mb-[18px]" : "mb-4"}`}>
          {editorial ? (
            <div>
              <span
                data-testid="members-kicker"
                className="text-[10px] uppercase"
                style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
              >
                {roster.length} on this map
              </span>
              <h1
                className="mt-1.5 text-[30px] italic leading-tight"
                style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
              >
                Members
              </h1>
            </div>
          ) : (
            <h2 className="text-sm font-bold" style={{ color: INK }}>
              Members · {teamName}
            </h2>
          )}
          {isLead ? (
            /* Lead: enabled invite button that opens InviteMemberDialog. */
            <button
              type="button"
              data-testid="members-invite"
              onClick={() => setInviteOpen(true)}
              className={`shrink-0 font-semibold ${
                editorial
                  ? "rounded-md px-4 py-2 text-[12px]"
                  : "rounded-md px-3 py-1.5 text-[11px] text-white"
              }`}
              style={editorial ? inviteEditorialStyle : inviteLegacyStyle}
            >
              + invite
            </button>
          ) : (
            <Tooltip content="Only a lead can invite members.">
              {/* A disabled <button> swallows pointer events, so the tooltip
                  attaches to a wrapping span. */}
              <span className="shrink-0">
                <button
                  type="button"
                  data-testid="members-invite"
                  data-seam
                  disabled
                  className={`cursor-not-allowed font-semibold ${
                    editorial
                      ? "rounded-md px-4 py-2 text-[12px] opacity-60"
                      : "rounded-md px-3 py-1.5 text-[11px] text-white opacity-70"
                  }`}
                  style={editorial ? inviteEditorialStyle : inviteLegacyStyle}
                >
                  + invite
                </button>
              </span>
            </Tooltip>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2" data-testid="members-loading">
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
          </div>
        ) : isError ? (
          <ErrorState
            title="Couldn't load members"
            message="Refresh the page; if it keeps happening, check your connection and try again."
          />
        ) : (
          <>
            {/* Editorial groups the rows into one warm `.t-card` (hairline
                dividers between rows); legacy keeps the gapped mint cards. */}
            <ul
              className={editorial ? "overflow-hidden border" : "flex flex-col gap-2"}
              data-testid="members-list"
              style={
                editorial
                  ? { backgroundColor: c.cardBg, borderColor: c.rule, borderRadius: theme.radii.lg }
                  : undefined
              }
            >
              {roster.map((member, i) => (
                <MemberRow
                  key={member.membershipId}
                  member={member}
                  index={i}
                  theme={theme}
                  last={i === roster.length - 1}
                />
              ))}
            </ul>
            {/* The brand guard, rendered verbatim from the gallery (§A9). */}
            {editorial ? (
              <p className="mt-5 text-center text-[12px] italic" style={{ color: c.muted }}>
                "on N tasks" counts assignments — it is not a ranking or a score.
              </p>
            ) : (
              <p
                className="mt-4 border-t pt-3 text-[11px]"
                style={{ color: MUTED, borderColor: "#F3F4F6" }}
              >
                "on N tasks" counts assignments — it is not a ranking or a score.
              </p>
            )}
          </>
        )}
        {isLead && teamId ? <EventJoinLinkCard teamId={teamId} teamName={teamName} /> : null}
      </div>

      {/* InviteMemberDialog is mounted at the root level so it can overlay
          the full page. It is only rendered when the user is a lead; for
          members this branch is unreachable (inviteOpen stays false). */}
      {isLead && teamId ? (
        <InviteMemberDialog
          teamId={teamId}
          accountId={accountId}
          open={inviteOpen}
          onOpenChange={handleInviteOpenChange}
        />
      ) : null}
    </div>
  );
}

function MemberRow({
  member,
  index,
  theme,
  last,
}: { member: RosterMember; index: number; theme: MapTheme; last?: boolean }) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;

  if (editorial) {
    // Editorial member row (`V_B64`): a quiet `.t-card` row inside the list
    // card — a saturated avatar disc (white initials, deterministic tone) ·
    // name + role sub · a mono "ON N TASKS" coverage tag — hairline-divided.
    const avatarFill = editorialTone(member.initials);
    return (
      <li
        data-testid="member-row"
        data-membership-id={member.membershipId}
        className="flex items-center gap-4 px-[18px] py-3.5"
        style={{ borderBottom: last ? undefined : `1px solid ${c.rule}` }}
      >
        <span
          title={member.name}
          data-testid="member-avatar"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
          style={{ backgroundColor: avatarFill, border: `1.5px solid ${c.cardBg}` }}
        >
          {member.initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-medium" style={{ color: c.ink }}>
            {member.name}
          </span>
          {member.isLead ? (
            <span
              data-testid="member-role-lead"
              className="mt-0.5 block text-[11.5px]"
              style={{ color: c.muted }}
            >
              lead
            </span>
          ) : (
            <span
              data-testid="member-role"
              className="mt-0.5 block text-[11.5px]"
              style={{ color: c.muted }}
            >
              member
            </span>
          )}
        </span>
        <span
          data-testid="member-task-count"
          className="shrink-0 text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.06em" }}
        >
          on {member.taskCount} {member.taskCount === 1 ? "task" : "tasks"}
        </span>
      </li>
    );
  }

  // The lead row is tinted (gallery: #F7F8FA fill on the first row); the rest
  // are plain white cards. Avatar fills alternate teal/indigo/slate the way
  // the gallery shows (lead teal, then indigo, then slate-grey).
  const avatarFill = member.isLead ? PALETTE.teal : index === 1 ? INDIGO : MUTED;
  return (
    <li
      data-testid="member-row"
      data-membership-id={member.membershipId}
      className="flex items-center gap-3 rounded-lg px-3 py-2.5"
      style={{
        backgroundColor: member.isLead ? "#F7F8FA" : "#FFFFFF",
        border: "1px solid #F0F2F5",
      }}
    >
      <span
        title={member.name}
        data-testid="member-avatar"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
        style={{ backgroundColor: avatarFill }}
      >
        {member.initials}
      </span>
      <span
        className="min-w-0 flex-1 truncate text-xs"
        style={{ color: INK, fontWeight: member.isLead ? 600 : 400 }}
      >
        {member.name}
      </span>
      {member.isLead ? (
        <span
          data-testid="member-role-lead"
          className="shrink-0 rounded px-2 py-0.5 text-[10px]"
          style={{ backgroundColor: "#EEF2FF", color: INDIGO }}
        >
          lead
        </span>
      ) : (
        <span data-testid="member-role" className="shrink-0 text-xs" style={{ color: MUTED }}>
          member
        </span>
      )}
      <span
        data-testid="member-task-count"
        className="w-20 shrink-0 text-right text-xs"
        style={{ color: MUTED }}
      >
        on {member.taskCount} {member.taskCount === 1 ? "task" : "tasks"}
      </span>
    </li>
  );
}
